import NeonHero from '../components/NeonHero';
import ServiceCard from '../components/ServiceCard';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import FeatureRow from '../components/FeatureRow';
import FinancingBanner from '../components/FinancingBanner';
import { Wrench, Cog, Gauge, Car, Paintbrush, Zap, Snowflake, Cpu, Settings2, Sun, Phone } from 'lucide-react';
import { motion } from 'framer-motion';
import GettingStartedVideo from '../assets/GettingStarted.mp4';
import allstate from '../assets/allstate.png';
import europcar from '../assets/europcar.png';
import geico from '../assets/geico.png';
import statefarm from '../assets/statefarm.png';
import hertz from '../assets/hertz.png';
import manheim from '../assets/manheim.png';
import installstandards from '../assets/installstandards.png';
import lifetimewarranty from '../assets/lifetimewarranty.png';
import safetymeasures from '../assets/safetymeasures.png';
import wshero from '../assets/wshero.png';
import fleet247 from '../assets/247fleet.JPG';

export default function Home() {
	const services = [
		{ title: 'Windshield Repair', description: 'Advanced AI system quickly assesses damage and automates the entire repair process.', to: '/services/windshield-repair', icon: <Car size={18} /> },
		{ title: 'Windshield Replacement', description: 'AI technology ensures perfect fit and sources cost-effective parts with rapid service.', to: '/services/windshield-replacement', icon: <Car size={18} /> },
		{ title: 'Side Glass Replacement', description: 'AI-powered systems accurately assess and identify exact glass specifications for any vehicle.', to: '/services/side-glass', icon: <Car size={18} /> },
		{ title: 'Roof & Back Glass', description: 'Expert installation with automated glass identification and sourcing for seamless experience.', to: '/services/roof-back-glass', icon: <Car size={18} /> },
		{ title: 'ADAS Calibrations', description: 'AI-driven diagnostics interface with vehicle systems for accurate recalibrations.', to: '/services/adas-calibrations', icon: <Cpu size={18} /> },
		{ title: 'Diagnostics & Coding', description: 'Real-time identification of recalibration requirements maintaining highest safety standards.', to: '/services/diagnostics-coding', icon: <Cpu size={18} /> },
	];

	return (
		<main>
		<section aria-label="Hero section" className="relative flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-black particle-container">
			{/* Animated Background Elements */}
			<div className="absolute inset-0 z-0">
				<div className="absolute top-20 left-20 w-32 h-32 bg-babyblue/20 rounded-full blur-xl animate-float"></div>
				<div className="absolute top-40 right-32 w-24 h-24 bg-royalpurple/20 rounded-full blur-xl animate-float" style={{ animationDelay: '2s' }}></div>
				<div className="absolute bottom-32 left-1/3 w-28 h-28 bg-orangepeel/20 rounded-full blur-xl animate-float" style={{ animationDelay: '4s' }}></div>
				<div className="absolute bottom-20 right-20 w-36 h-36 bg-babyblue/15 rounded-full blur-xl animate-float" style={{ animationDelay: '1s' }}></div>
			</div>

			<div className="container relative z-10 h-full flex items-center justify-center pt-16 pb-4 md:py-4">
				<div className="text-center w-full max-w-5xl mx-auto h-full flex flex-col items-center justify-center">
					<motion.h1 
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, ease: "easeOut" }}
						className="text-3xl md:text-5xl font-extrabold text-gradient mb-4"
					>
						Fix your glass fast with A.I.
					</motion.h1>
					<motion.p 
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
						className="text-lg md:text-xl text-gray-300 max-w-4xl mx-auto mb-8"
					>
						AI-powered part identification, automated parts sourcing, technician routing, and quality control for faster, more cost-effective glass services.
					</motion.p>
					
					<motion.div 
						initial={{ opacity: 0, scale: 0.9, y: 20 }}
						animate={{ opacity: 1, scale: 1, y: 0 }}
						transition={{ duration: 0.9, delay: 0.8, ease: "easeOut" }}
						className="flex justify-center mb-6"
					>
						<div className="w-[min(80vw,20rem)] bg-black rounded-[2.5rem] p-4 shadow-2xl border border-black/30">
							<div className="aspect-[9/19.5] rounded-[2rem] overflow-hidden bg-black">
								<video 
									src={GettingStartedVideo} 
									autoPlay 
									loop 
									muted 
									playsInline
									className="w-full h-full object-cover"
									aria-label="Ubiquity AutoGlass AI-powered glass identification and repair process demonstration"
									title="AI-Powered Glass Repair Process"
								>
									Your browser does not support the video tag.
								</video>
							</div>
						</div>
					</motion.div>


					<motion.div 
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, delay: 1.2, ease: "easeOut" }}
						className="flex gap-3 sm:gap-4 justify-center mb-6 flex-wrap"
					>
						<motion.a 
							href="https://getstarted.ubiquityglass.com" 
							target="_blank" 
							rel="noreferrer"
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
						>
							<Button className="animated-gradient-fast subtle-glow text-white px-6 py-3 text-base font-semibold hover:opacity-90">
								Get Started
							</Button>
						</motion.a>
						<motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
							<Link to="/services">
								<Button variant="outline" className="border-white text-white px-6 py-3 text-base font-semibold hover:bg-white/10 bg-transparent">
									Learn More
								</Button>
							</Link>
						</motion.div>
					</motion.div>

					{/* video moved above bullets */}
				</div>
			</div>
		</section>

		<FinancingBanner />

		{/* Emergency Glass Repair */}
		<section className="py-12 md:py-16 bg-red-50 border-t border-red-200">
			<div className="container">
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.8 }}
					className="text-center"
				>
					<div className="max-w-2xl mx-auto">
						<div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
							<Phone className="w-8 h-8 text-red-600" />
						</div>
						<h2 className="text-2xl font-bold text-red-900 mb-4">Emergency Glass Repair</h2>
						<p className="text-red-700 mb-6">
							Need immediate assistance? Our emergency team is available 24/7 for urgent glass repairs and replacements.
						</p>
						<div className="flex justify-center">
							<a href="tel:8445820750">
								<Button className="bg-red-600 hover:bg-red-700 text-white">
									<Phone className="w-4 h-4 mr-2" />
									Call Emergency Line: (844) 582-0750
								</Button>
							</a>
						</div>
					</div>
				</motion.div>
			</div>
		</section>

			<FeatureRow
				title="AI-Powered Glass Identification with Iris"
				desc="Our advanced Iris technology uses computer vision and machine learning to instantly identify glass damage, assess repair feasibility, and provide accurate estimates in real-time."
				bullets={["Instant damage assessment","AI-powered glass identification","Real-time repair estimates","Automated parts sourcing"]}
				media={<img src={wshero} alt="Iris AI Glass Identification Technology" className="w-full h-auto rounded-xl shadow-lg border border-gray-200" />}
			/>

			<section className="container py-12 md:py-20">
				<h2 className="text-2xl md:text-3xl font-bold">AI-Powered Glass Services</h2>
				<p className="mt-2 text-gray-600 max-w-2xl">Advanced AI technology automates damage assessment, parts sourcing, and technician routing for faster, more cost-effective glass repair and replacement.</p>
				<div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
					{services.map((s) => (
						<Link key={s.title} to={s.to} className="block">
							<ServiceCard title={ s.title } description={ s.description } icon={s.icon} />
						</Link>
					))}
				</div>
			</section>

			<FeatureRow
				reverse
				title="24/7 Fleet Automotive Glass Services"
				desc="Round-the-clock automotive glass services for fleet operators across the automotive industry. Our AI-powered system ensures continuous availability and rapid response for all fleet glass needs."
				bullets={["24/7 glass repair & replacement","Fleet operator partnerships","Automotive industry expertise","Rapid response times","Continuous service availability"]}
				media={
					<div className="w-full aspect-square rounded-xl overflow-hidden shadow-lg border border-gray-200">
						<img src={fleet247} alt="24/7 Fleet Automotive Glass Services" className="w-full h-full object-cover" />
					</div>
				}
			/>

			<section className="animated-gradient-slow particle-container subtle-glow py-12 md:py-16">
				<div className="container text-white text-center">
					<h3 className="text-2xl font-bold mb-4">Need Complete Automotive Services?</h3>
					<p className="mb-6 max-w-2xl mx-auto">For comprehensive vehicle services beyond automotive glass, visit our sister company Ubiquity Automotive for engine, transmission, suspension, body work, electrical, AC, programming, tuning, and aftermarket modifications.</p>
					<div className="flex gap-3 justify-center">
						<a href="https://ubiquityauto.com" target="_blank" rel="noreferrer"><Button className="bg-white text-secondary hover:opacity-90">Visit Ubiquity Automotive</Button></a>
						<Link to="/services"><Button variant="outline" className="border-white text-white hover:bg-white/10 bg-transparent">Our Glass Services</Button></Link>
					</div>
				</div>
			</section>

			<section className="bg-white dark:bg-transparent border-t dark:border-white/10">
				<div className="container py-14 text-center">
					<h3 className="text-2xl md:text-3xl font-bold mb-4">Over 10K vehicles serviced with ZERO warranties.</h3>
					<p className="text-gray-600 max-w-3xl mx-auto mb-8">Having been trusted by global brands, Ubiquity AutoGlass has a track record of precision, innovation, and efficiency through every service we've provided. With our advanced AI-driven technology and a commitment to excellence, Ubiquity AutoGlass consistently delivers unmatched quality and reliability.</p>
					<div className="grid md:grid-cols-4 gap-8 mb-12">
						<div className="text-center">
							<div className="text-4xl font-extrabold text-gradient mb-2">10K+</div>
							<div className="text-gray-600">Vehicles serviced</div>
						</div>
						<div className="text-center">
							<div className="text-4xl font-extrabold text-gradient mb-2">0</div>
							<div className="text-gray-600">Warranty claims</div>
						</div>
						<div className="text-center">
							<div className="text-4xl font-extrabold text-gradient mb-2">100%</div>
							<div className="text-gray-600">Customer satisfaction</div>
						</div>
						<div className="text-center">
							<div className="text-4xl font-extrabold text-gradient mb-2">Global</div>
							<div className="text-gray-600">Brand Trust</div>
						</div>
					</div>
					<div className="mb-12">
						<h4 className="text-lg font-semibold mb-6">Trusted by brands all over the world</h4>
						<div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
							<img src={allstate} alt="Allstate" className="h-8" />
							<img src={statefarm} alt="State Farm" className="h-8" />
							<img src={geico} alt="GEICO" className="h-8" />
							<img src={hertz} alt="Hertz" className="h-8" />
							<img src={europcar} alt="Europcar" className="h-8" />
							<img src={manheim} alt="Manheim" className="h-8" />
						</div>
					</div>
				</div>
			</section>

			<section className="bg-gray-50 py-14">
				<div className="container">
					<h3 className="text-2xl md:text-3xl font-bold text-center mb-12">Safety & Security</h3>
					<div className="grid md:grid-cols-3 gap-8">
						<div className="text-center">
							<div className="w-56 h-56 mx-auto mb-8 flex items-center justify-center">
								<img src={installstandards} alt="Installation Standards" className="w-full h-full object-contain" />
							</div>
							<h4 className="text-lg font-semibold mb-3">Advanced Installation Standards</h4>
							<p className="text-gray-600">Ubiquity strictly adheres to installation standards that meet or exceed the safety regulations set by the National Highway Traffic Safety Administration (NHTSA) and the Federal Motor Vehicle Safety Standards (FMVSS). This ensures that every installation is performed with the highest level of safety in mind.</p>
						</div>
						<div className="text-center">
							<div className="w-56 h-56 mx-auto mb-8 flex items-center justify-center">
								<img src={lifetimewarranty} alt="Lifetime Warranty" className="w-full h-full object-contain" />
							</div>
							<h4 className="text-lg font-semibold mb-3">Lifetime Warranty</h4>
							<p className="text-gray-600">Ubiquity offers a lifetime workmanship warranty, and is covered by 3rd party insurers on all services we provide. This means that if there are any issues with the installation or the glass, we will take care of it, because we believe in providing customers with long-term assurance, protection and peace of mind.</p>
						</div>
						<div className="text-center">
							<div className="w-56 h-56 mx-auto mb-8 flex items-center justify-center">
								<img src={safetymeasures} alt="Safety Measures" className="w-full h-full object-contain" />
							</div>
							<h4 className="text-lg font-semibold mb-3">Technology-Driven Safety Measures</h4>
							<p className="text-gray-600">Ubiquity deployed Frontline on iOS / Android to ensure precise installation procedures, minimizing errors and enhancing safety. Frontline automates identity verification, background checks, and insurance coverage, ensuring only trusted technicians handle installations.</p>
						</div>
					</div>
				</div>
			</section>

			<FinancingBanner />


			<section className="container py-14">
				<h3 className="text-xl md:text-2xl font-semibold">Common questions</h3>
				<div className="mt-6 divide-y">
					<details className="group py-4">
						<summary className="cursor-pointer list-none flex items-center justify-between">
							<span className="font-medium">How does the AI damage assessment work?</span>
							<span className="text-gradient">+</span>
						</summary>
						<p className="mt-2 text-gray-600">Our advanced AI system quickly assesses the extent of damage, ensuring that repair is the best option for your vehicle. It automates the entire process from damage detection to material procurement.</p>
					</details>
					<details className="group py-4">
						<summary className="cursor-pointer list-none flex items-center justify-between">
							<span className="font-medium">Do you work with insurance companies?</span>
							<span className="text-gradient">+</span>
						</summary>
						<p className="mt-2 text-gray-600">Yes. We coordinate estimates and approvals with insurance carriers and provide comprehensive insurance claims management services.</p>
					</details>
					<details className="group py-4">
						<summary className="cursor-pointer list-none flex items-center justify-between">
							<span className="font-medium">What warranty do you provide?</span>
							<span className="text-gradient">+</span>
						</summary>
						<p className="mt-2 text-gray-600">Lifetime workmanship warranty on all services we provide, covered by 3rd party insurers for complete peace of mind.</p>
					</details>
					<details className="group py-4">
						<summary className="cursor-pointer list-none flex items-center justify-between">
							<span className="font-medium">Do you handle ADAS calibrations?</span>
							<span className="text-gradient">+</span>
						</summary>
						<p className="mt-2 text-gray-600">Yes. Our AI-driven system interfaces directly with your vehicle's onboard diagnostics, ensuring all ADAS components are recalibrated accurately to maintain safety standards.</p>
					</details>
				</div>
				<div className="mt-8 flex gap-3">
					<a href="https://getstarted.ubiquityglass.com" target="_blank" rel="noreferrer"><Button variant="gradient" size="lg">Get Started</Button></a>
					<Link to="/support"><Button variant="outline" size="lg">Contact Sales</Button></Link>
				</div>
			</section>
		</main>
	);
}
